import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFirestoreCollection } from '../../hooks/useFirestoreCollection';
import { FirebaseImage } from '../FirebaseImage';
import type { TireBrand } from '../../types/firebase';

const TireBrandsSection: React.FC = () => {
  const { t } = useLanguage();

  const { data: brands } = useFirestoreCollection<TireBrand>({
    collection: 'tire-brands',
    where: [['published', '==', true]],
    orderBy: ['order', 'asc'],
  });

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-4">{t('tires.brands.title')}</h3>
          <div className="w-16 h-1 bg-gradient-to-r from-red-600 to-red-500 mx-auto rounded-full mb-6" />
          <p className="text-gray-600 text-lg">{t('tires.brands.subtitle')}</p>
        </motion.div>

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-6 items-center justify-items-center">
          {brands.map((brand, index) => (
            <motion.div
              key={brand.id}
              initial={{ opacity: 0, scale: 0.5, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.6,
                delay: 0.1 * index,
                type: "spring",
                damping: 15
              }}
              whileHover={{
                scale: 1.2,
                y: -10,
                rotateY: 10,
                transition: { duration: 0.3 }
              }}
              className="relative group"
            >
              <div className="relative p-4 bg-white rounded-2xl border border-gray-200 shadow-lg group-hover:shadow-xl transition-all duration-300 w-28 h-28">

                <FirebaseImage
                  src={brand.logoUrl}
                  alt={`Logo ${brand.name}`}
                  className="relative z-10 w-full h-full object-contain transition-all duration-300"
                />

                {/* Brand Name Tooltip */}
                <div className="absolute -bottom-14 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                  <div className="bg-black/80 text-white text-sm font-medium px-3 py-1 rounded-lg whitespace-nowrap">
                    {brand.name}
                  </div>
                  <div className="w-2 h-2 bg-black/80 transform rotate-45 mx-auto -mt-1" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Brands Indicator */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 1 }}
          className="text-center mt-12"
        >
          <motion.div
            animate={{ 
              scale: [1, 1.05, 1],
              opacity: [0.7, 1, 0.7]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20"
          >
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              <div className="w-2 h-2 bg-red-500 rounded-full" />
            </div>
            <span className="text-gray-700 font-medium">{t('tires.brands.more')}</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default TireBrandsSection;